import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ChartBarIcon,
  CubeIcon,
  ShoppingBagIcon,
  ArrowPathIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon,
  ArrowRightOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  UserCircleIcon,
  EyeIcon,
  SparklesIcon,
  TrophyIcon,
  TruckIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { logout } from '../../store/slices/authSlice';
import Logo from '../common/Logo';

const LabSidebar = ({ isOpen, setIsOpen, isDesktop }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, lab } = useSelector((state) => state.auth);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Check if lab has active premium subscription
  const isLabPremium = lab?.isPremium && lab?.premiumUntil && new Date(lab.premiumUntil) > new Date();

  const menuItems = [
    {
      name: t('lab.dashboard'),
      path: '/lab',
      icon: ChartBarIcon,
      exact: true
    },
    {
      name: t('lab.products'),
      path: '/lab/products',
      icon: CubeIcon
    },
    {
      name: t('labSidebar.incomingExchangeRequests'),
      path: '/lab/exchange-offers',
      icon: ArrowPathIcon
    },
    {
      name: t('labSidebar.activeExchanges'),
      path: '/lab/active-exchanges',
      icon: TruckIcon
    },
    {
      name: t('lab.exchangeRequests'),
      path: '/lab/exchange-requests',
      icon: EyeIcon
    },
    {
      name: t('lab.orders'),
      path: '/lab/orders',
      icon: ShoppingBagIcon
    },
    {
      name: t('lab.wallet'),
      path: '/lab/wallet',
      icon: BanknotesIcon
    },
    // Add subscription management for premium labs
    ...(isLabPremium ? [{
      name: t('lab.subscription'),
      path: '/lab/subscription',
      icon: SparklesIcon,
      isPremium: true
    }] : []),
    {
      name: t('lab.settings'),
      path: '/lab/settings',
      icon: Cog6ToothIcon
    }
  ];

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const isActiveRoute = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const sidebarVariants = {
    open: { x: 0 },
    closed: { x: '100%' }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0 }
  };

  // Show overlay only on mobile when sidebar is open
  const showOverlay = !isDesktop && isOpen;
  
  // Sidebar should be visible when:
  // - Desktop: always visible
  // - Mobile: when isOpen is true
  const sidebarVisible = isDesktop || isOpen;

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {showOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={`
          ${isDesktop ? 'relative' : 'fixed'} 
          top-0 right-0 h-full bg-white shadow-lg z-50
          transition-all duration-300 ease-in-out
          ${isCollapsed && isDesktop ? 'w-20' : 'w-72'}
          ${sidebarVisible ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            {!isCollapsed && (
              <div className="flex items-center gap-3">
                <Logo className="h-8 w-auto" />
                <span className="text-xl font-bold text-gray-900">
                  {t('lab.dashboard')}
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              {/* Collapse button for desktop */}
              {isDesktop && (
                <button
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {isCollapsed ? (
                    <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              )}
              
              {/* Close button for mobile */}
              {!isDesktop && (
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-600" />
                </button>
              )}
            </div>
          </div>

          {/* User Info */}
          <div className="block">{/* Always show */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-secondary-50">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-brand-blue to-brand-blueSecondary rounded-full flex items-center justify-center">
                  <UserCircleIcon className="h-8 w-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {user?.name || 'المختبر'}
                    </h3>
                    {/* Premium Badge */}
                    {lab?.isPremium && lab?.premiumUntil && new Date(lab.premiumUntil) > new Date() && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-brand-blue to-brand-green rounded-full">
                        <TrophyIcon className="h-3 w-3 text-white" />
                        <span className="text-xs font-bold text-white">بلس</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{t('lab.labManager')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6">
            <motion.div
              variants={{
                visible: {
                  transition: { staggerChildren: 0.05 }
                }
              }}
              initial="hidden"
              animate="visible"
              className="space-y-2 px-4"
            >
              {menuItems.map((item, index) => {
                const isActive = isActiveRoute(item.path, item.exact);
                return (
                  <motion.div key={item.path} variants={itemVariants}>
                    <Link
                      to={item.path}
                      onClick={() => !isDesktop && setIsOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                        isActive
                          ? item.isPremium 
                            ? 'bg-gradient-to-r from-brand-blue to-brand-green text-white shadow-lg shadow-brand-blue/25'
                            : 'bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white shadow-lg'
                          : item.isPremium
                            ? 'text-gray-700 hover:bg-gradient-to-r hover:from-brand-blue/10 hover:to-brand-green/10 hover:text-brand-blue border border-brand-blue/20'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <item.icon className={`h-6 w-6 transition-colors ${
                        isActive 
                          ? 'text-white' 
                          : item.isPremium
                            ? 'text-brand-blue group-hover:text-brand-blue'
                            : 'text-gray-500 group-hover:text-gray-700'
                      }`} />
                      <span className="font-medium">{item.name}</span>
                      {item.isPremium && (
                        <TrophyIcon className={`h-4 w-4 ${
                          isActive ? 'text-white' : 'text-brand-green'
                        }`} />
                      )}
                      
                      {isActive && (
                        <motion.div
                          layoutId="activeIndicator"
                          className="ml-auto w-2 h-2 bg-white rounded-full"
                        />
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </nav>

          {/* Back to Site Button */}
          <div className="p-4 border-t border-gray-200">
            <Link
              to="/"
              onClick={() => !isDesktop && setIsOpen(false)}
              className="flex items-center gap-3 w-full px-4 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-xl hover:opacity-95 transition-all duration-200 group"
            >
              <HomeIcon className="h-6 w-6" />
              <span className="font-medium">{t('common.backToSite', 'العودة إلى الموقع')}</span>
            </Link>
          </div>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 group"
            >
              <ArrowRightOnRectangleIcon className="h-6 w-6" />
              <span className="font-medium">{t('lab.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LabSidebar;
